import { useMemo, useState } from "react";

import moment from "moment";

import {
  GroupedNotifications,
  type MMMNotification,
  NotificationDismissMode,
  NotificationType,
} from "@features/mmm/components/notifications/grouped-notifications";
import { getDate } from "@features/mmm/utils/formatters";
import { MMMClientState } from "@north-beam/nb-common";
import { useAdoptedMix } from "@pages/mmm/hooks/use-adopted-mix";
import { useAlerts } from "@pages/mmm/hooks/use-alerts";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { PerformanceTrackingProvider } from "../performance-tracking/hooks/performance-tracking-provider";

import { BudgetPacingModal } from "./budget-pacing";

export function BudgetPacingNotificationPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedCycle: cycle } = useSelectedCycle();
  const [alerts, , { dismissAlert }] = useAlerts();
  const [showNoAlertCta, setShowNoAlertCta] = useState(true);
  const [adoptedBudget] = useAdoptedMix();

  const cycleAlerts = alerts.filter(
    (alert) => alert.cycle === cycle?.id && !alert.dismissed_at,
  );
  const createUnderpacingAlertSummaryNotification = (
    alertIds: string[],
  ): MMMNotification => {
    const ALERT_SUMMARY_ID = "MMM_MIDCYCLE_UNDERPACING_ALERTS_EXIST_SUMMARY";
    return {
      id: ALERT_SUMMARY_ID,
      mainText: "You are spending less than planned in some channels.",
      subText: "",
      type: NotificationType.WARNING,
      onDismiss: () => {
        // TODO: Refactor the API to allow dismissing multiple alerts in one
        // API call.
        alertIds.forEach((id) => dismissAlert(id));
      },
      dismissMode: NotificationDismissMode.SHOW,
      pill: {
        onClick: () => setIsOpen(true),
        text: "UPDATE BUDGET",
      },
      isActionable: true,
    };
  };

  const shouldShowAlerts = useMemo(() => {
    return (
      cycle?.state === MMMClientState.TRACKING_MIX &&
      showNoAlertCta &&
      adoptedBudget &&
      getDate(cycle?.date_range.end_date) > moment()
    );
  }, [cycle?.state, showNoAlertCta, adoptedBudget, cycle?.date_range.end_date]);
  return (
    <PerformanceTrackingProvider>
      {cycleAlerts.length > 0 ? (
        <GroupedNotifications
          notifications={[
            createUnderpacingAlertSummaryNotification(
              cycleAlerts.map((alert) => alert.id),
            ),
          ]}
        />
      ) : (
        shouldShowAlerts && (
          <GroupedNotifications
            notifications={[
              {
                id: "no-budget-pacing-alerts-cta",
                mainText: "No underpacing alerts",
                subText: "",
                type: NotificationType.INFO,
                dismissMode: NotificationDismissMode.SHOW,
                onDismiss: () => setShowNoAlertCta(false),
                pill: {
                  onClick: () => setIsOpen(true),
                  text: "UPDATE BUDGET",
                },
                isActionable: true,
              },
            ]}
          />
        )
      )}
      <BudgetPacingModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </PerformanceTrackingProvider>
  );
}
